import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, Package } from 'lucide-react';
import axios from 'axios';

const ProductSelect = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const comboboxRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  // Auto-focus the combobox when component mounts
  useEffect(() => {
    if (comboboxRef.current && !loading) {
      comboboxRef.current.focus();
    }
  }, [loading]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (comboboxRef.current && !comboboxRef.current.contains(event.target)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch products from the device types API
      const response = await axios.get('/api/device-types');
      setProducts(response.data);
      
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  // Filter products based on search term
  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleProductSelect = (productName) => {
    // Navigate to device edit with the selected product
    navigate(`/devices/new?product=${encodeURIComponent(productName)}`);
  };

  // Removed auto-scrolling of the highlighted option to avoid jumpy UI

  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setSelectedIndex(0);
        } else {
          setSelectedIndex(prev => 
            prev < filteredProducts.length - 1 ? prev + 1 : prev
          );
        }
        break;
      
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
        }
        break;
      
      case 'Enter':
        e.preventDefault();
        if (isOpen && selectedIndex >= 0 && filteredProducts[selectedIndex]) {
          handleProductSelect(filteredProducts[selectedIndex].name);
        } else if (!isOpen && filteredProducts.length > 0) {
          handleProductSelect(filteredProducts[0].name);
        }
        break;
      
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
      
      default:
        if (!isOpen && e.key.length === 1) {
          setIsOpen(true);
          setSelectedIndex(0);
        }
        break;
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsOpen(true);
    setSelectedIndex(0);
  };

  const handleOptionClick = (product, index) => {
    setSelectedIndex(index);
    handleProductSelect(product.name);
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center">
            <button
              onClick={handleCancel}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600"
            >
              <ArrowLeft size={24} />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Select Product</h1>
              <p className="mt-1 text-sm text-gray-500">
                Choose a product type for your new device
              </p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Product Combobox */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Product</h2>
          
          {products.length === 0 ? (
            <div className="text-center py-8">
              <Package className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No products available</h3>
              <p className="mt-1 text-sm text-gray-500">
                No products have been configured yet. Please configure products first.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/catalog/products')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-naviter-blue hover:bg-naviter-blue-dark"
                >
                  Configure Products
                </button>
              </div>
            </div>
          ) : (
            <div className="relative" ref={comboboxRef}>
              {/* Combobox Input */}
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Type to search products..."
                  className="w-full px-4 py-3 text-lg border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  autoComplete="off"
                  role="combobox"
                  aria-expanded={isOpen}
                  aria-haspopup="listbox"
                />
                <button
                  type="button"
                  onClick={() => setIsOpen(!isOpen)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600"
                >
                  {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
              </div>

              {/* Dropdown List */}
              {isOpen && (
                <div 
                  ref={listRef}
                  className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
                  role="listbox"
                >
                  {filteredProducts.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                      No products match "{searchTerm}"
                    </div>
                  ) : (
                    filteredProducts.map((product, index) => (
                      <div
                        key={product.id}
                        role="option"
                        aria-selected={index === selectedIndex}
                        className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                          index === selectedIndex
                            ? 'bg-indigo-50 text-indigo-900'
                            : 'text-gray-900 hover:bg-gray-50'
                        }`}
                        onClick={() => handleOptionClick(product, index)}
                        onMouseEnter={() => setSelectedIndex(index)}
                      >
                        <div className="font-medium">{product.name}</div>
                        <div className="text-sm text-gray-500 mt-1">
                          {product.attribute_count} attributes • {product.module_count} modules
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Keyboard Navigation
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p className="mb-2">Use keyboard shortcuts for fast selection:</p>
                <ul className="list-disc list-inside space-y-1 mb-3">
                  <li>Type to filter products (e.g., "Oudie" shows all Oudie variants)</li>
                  <li><kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">↓↑</kbd> Arrow keys to navigate options</li>
                  <li><kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Enter</kbd> to select highlighted product</li>
                  <li><kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Esc</kbd> to close dropdown</li>
                </ul>
                <p>After selecting a product, you'll configure device details and attributes.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductSelect;
